import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle, Briefcase, Building2, Phone, Target, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AgentOnboardingProps {
  onComplete: (data: AgentFormData) => void;
  onBack: () => void;
  isCompletion?: boolean;
}

interface AgentFormData {
  professionalName: string;
  agencyName: string;
  yearsExperience: string;
  specialization: string[];
  commissionRate: string;
  totalDeals: string;
  successRate: string;
  contactInfo: {
    phone: string;
    email: string;
    website: string;
  };
  services: string;
  professionalBio: string;
}

const initialFormData: AgentFormData = {
  professionalName: '',
  agencyName: '',
  yearsExperience: '',
  specialization: [],
  commissionRate: '',
  totalDeals: '',
  successRate: '',
  contactInfo: {
    phone: '',
    email: '',
    website: '',
  },
  services: '',
  professionalBio: '',
};

const specializationOptions = [
  'Football', 'Basketball', 'Baseball', 'Soccer', 'Tennis', 'Golf',
  'Hockey', 'Swimming', 'Track & Field', 'Boxing', 'MMA', 'Gymnastics',
  'Entertainment', 'Music', 'Influencer Marketing', 'Brand Partnerships'
];

const experienceRanges = [
  '0-1 years', '2-3 years', '4-5 years', '6-10 years', '10+ years'
];

const steps = [
  { id: 1, title: 'Personal Info', icon: User },
  { id: 2, title: 'Agency Details', icon: Building2 },
  { id: 3, title: 'Track Record', icon: Target },
  { id: 4, title: 'Contact & Services', icon: Phone },
  { id: 5, title: 'Review', icon: CheckCircle }
];

export const EnhancedAgentOnboarding: React.FC<AgentOnboardingProps> = ({
  onComplete,
  onBack,
  isCompletion = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<AgentFormData>(initialFormData);
  const [newSpecialization, setNewSpecialization] = useState('');
  const [loading, setLoading] = useState(false);

  const progress = (currentStep / steps.length) * 100;

  const updateFormData = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof AgentFormData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const addSpecialization = () => {
    if (newSpecialization.trim() && !formData.specialization.includes(newSpecialization.trim())) {
      updateFormData('specialization', [...formData.specialization, newSpecialization.trim()]);
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (index: number) => {
    updateFormData('specialization', formData.specialization.filter((_, i) => i !== index));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in first');
        return;
      }

      let { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) {
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            email: user.email || '',
            display_name: formData.professionalName || user.email?.split('@')[0] || 'Agent',
            account_type: 'agent',
            setup_completed: true
          })
          .select('id')
          .single();

        if (profileError) {
          console.error('Error creating profile:', profileError);
          toast.error('Failed to create profile. Please try again.');
          return;
        }
        profile = newProfile;
      }

      // Prepare agent data - allow empty values
      const agentData = {
        profile_id: profile.id,
        professional_name: formData.professionalName || 'Agent',
        agency_name: formData.agencyName || '',
        years_experience: formData.yearsExperience ? parseInt(formData.yearsExperience.split('-')[0]) || 0 : 0,
        specialization: formData.specialization.length > 0 ? formData.specialization : [],
        commission_rate: formData.commissionRate ? parseFloat(formData.commissionRate) || 0 : 0,
        total_deals: formData.totalDeals ? parseInt(formData.totalDeals) || 0 : 0,
        success_rate: formData.successRate ? parseFloat(formData.successRate) || 0 : 0,
        contact_info: formData.contactInfo,
        professional_bio: formData.professionalBio || '',
        professional_email: formData.contactInfo.email || '',
        phone: formData.contactInfo.phone || '',
        services_offered: formData.services || '',
      };

      const { error } = await supabase
        .from('agents')
        .insert(agentData);

      if (error) {
        console.error('Error creating agent:', error);
        toast.error('Failed to create agent profile');
        return;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          email: user.email || '',
          display_name: formData.professionalName || user.email?.split('@')[0] || 'Agent',
          account_type: 'agent',
          setup_completed: true 
        })
        .eq('id', profile.id);

      if (updateError) {
        console.error('Error updating profile:', updateError);
        toast.error('Failed to update profile. Please try again.');
        return;
      }

      toast.success('Agent profile created successfully!');
      onComplete(formData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <User className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Personal Information</h3>
              <p className="text-muted-foreground text-sm">
                Tell us about yourself as a professional agent
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="professionalName">Professional Name *</Label>
              <Input
                id="professionalName"
                placeholder="e.g., John Smith"
                value={formData.professionalName}
                onChange={(e) => updateFormData('professionalName', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="professionalBio">Professional Bio</Label>
              <Textarea
                id="professionalBio"
                placeholder="Brief description of your background and expertise..."
                value={formData.professionalBio}
                onChange={(e) => updateFormData('professionalBio', e.target.value)}
                rows={4}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Building2 className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Agency Information</h3>
              <p className="text-muted-foreground text-sm">
                Details about your agency or company
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="agencyName">Agency/Company Name *</Label>
              <Input
                id="agencyName"
                placeholder="e.g., CAA Sports, WME Sports"
                value={formData.agencyName}
                onChange={(e) => updateFormData('agencyName', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="yearsExperience">Years of Experience *</Label>
              <Select value={formData.yearsExperience} onValueChange={(value) => updateFormData('yearsExperience', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select experience range" />
                </SelectTrigger>
                <SelectContent>
                  {experienceRanges.map((range) => (
                    <SelectItem key={range} value={range}>
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Areas of Specialization *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Add the sports or entertainment areas you specialize in
                </p>
                <div className="flex gap-2 mb-3">
                  <Select value={newSpecialization} onValueChange={setNewSpecialization}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select specialization" />
                    </SelectTrigger>
                    <SelectContent>
                      {specializationOptions.map((spec) => (
                        <SelectItem key={spec} value={spec}>
                          {spec}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={addSpecialization} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.specialization.map((spec, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeSpecialization(index)}
                    >
                      {spec} ×
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Target className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Professional Track Record</h3>
              <p className="text-muted-foreground text-sm">
                Share your track record and professional metrics
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="commissionRate">Commission Rate (%)</Label>
                <Input
                  id="commissionRate"
                  type="number"
                  placeholder="e.g., 10"
                  value={formData.commissionRate}
                  onChange={(e) => updateFormData('commissionRate', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="successRate">Success Rate (%)</Label>
                <Input
                  id="successRate"
                  type="number"
                  placeholder="e.g., 85"
                  value={formData.successRate}
                  onChange={(e) => updateFormData('successRate', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="totalDeals">Total Deals Closed</Label>
              <Input
                id="totalDeals"
                type="number"
                placeholder="e.g., 150"
                value={formData.totalDeals}
                onChange={(e) => updateFormData('totalDeals', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="services">Services Offered</Label>
              <Textarea
                id="services"
                placeholder="Describe the services you offer to your clients..."
                value={formData.services}
                onChange={(e) => updateFormData('services', e.target.value)}
                rows={4}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Phone className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Contact Information</h3>
              <p className="text-muted-foreground text-sm">
                How can potential clients reach you?
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contactPhone">Phone Number *</Label>
                <Input
                  id="contactPhone"
                  placeholder="(555) 123-4567"
                  value={formData.contactInfo.phone}
                  onChange={(e) => updateFormData('contactInfo.phone', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Business Email *</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  placeholder="agent@agency.com"
                  value={formData.contactInfo.email}
                  onChange={(e) => updateFormData('contactInfo.email', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contactWebsite">Website</Label>
                <Input
                  id="contactWebsite"
                  placeholder="https://www.youragency.com"
                  value={formData.contactInfo.website}
                  onChange={(e) => updateFormData('contactInfo.website', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <CheckCircle className="h-12 w-12 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Review Your Agent Profile</h3>
              <p className="text-muted-foreground">
                Please review all information before creating your profile
              </p>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {formData.professionalName || 'Agent Profile'}
                </CardTitle>
                <CardDescription>
                  {formData.agencyName} • {formData.yearsExperience} experience
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.professionalBio && (
                  <div>
                    <h4 className="font-medium mb-1">Professional Bio</h4>
                    <p className="text-sm text-muted-foreground">{formData.professionalBio}</p>
                  </div>
                )}
                
                {formData.specialization.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Specializations</h4>
                    <div className="flex flex-wrap gap-1">
                      {formData.specialization.map((spec, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Commission Rate:</span>
                    <p className="text-muted-foreground">{formData.commissionRate}%</p>
                  </div>
                  <div>
                    <span className="font-medium">Success Rate:</span>
                    <p className="text-muted-foreground">{formData.successRate}%</p>
                  </div>
                  <div>
                    <span className="font-medium">Total Deals:</span>
                    <p className="text-muted-foreground">{formData.totalDeals}</p>
                  </div>
                  <div>
                    <span className="font-medium">Phone:</span>
                    <p className="text-muted-foreground">{formData.contactInfo.phone}</p>
                  </div>
                </div>
                
                {formData.services && (
                  <div>
                    <h4 className="font-medium mb-1">Services</h4>
                    <p className="text-sm text-muted-foreground">{formData.services}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-2xl space-y-8">
        <div className="text-center space-y-4 pt-8">
          <h1 className="text-3xl font-bold text-foreground">
            Agent Profile Setup
          </h1>
          <p className="text-muted-foreground">
            Create your comprehensive agent profile
          </p>
        </div>

        <div className="space-y-4">
          <Progress value={progress} className="w-full" />
          <div className="flex justify-between text-sm text-muted-foreground">
            {steps.map((step, index) => (
              <div key={step.id} className={`flex items-center gap-1 ${currentStep >= step.id ? 'text-primary' : ''}`}>
                <step.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            {renderStepContent()}
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={currentStep === 1 ? onBack : prevStep}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {currentStep === 1 ? (isCompletion ? 'Back' : 'Back to Roles') : 'Previous'}
            </Button>
            {isCompletion && (
              <Button 
                variant="ghost" 
                onClick={() => window.location.href = '/profile'}
                className="flex items-center gap-2"
              >
                Return to Profile
              </Button>
            )}
          </div>
          
          {currentStep < steps.length ? (
            <Button onClick={nextStep} className="flex items-center gap-2">
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading ? 'Creating...' : 'Complete Setup'}
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};